import { useEffect, useRef, useState, useMemo } from 'react';

interface PerformanceMetrics {
  renderTime: number;
  componentMounts: number;
  reRenders: number;
  memoryUsage?: number;
}

export function usePerformance(componentName: string = 'Component') {
  const renderStart = useRef<number>(Date.now());
  const mountTime = useRef<number>(0);
  const renderCount = useRef<number>(0);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    renderTime: 0,
    componentMounts: 0,
    reRenders: 0
  });

  // Track component mounts
  useEffect(() => {
    mountTime.current = Date.now();
    setMetrics(prev => ({
      ...prev,
      componentMounts: prev.componentMounts + 1
    }));

    // Cleanup function to log performance metrics
    return () => {
      const unmountTime = Date.now();
      const totalTime = unmountTime - mountTime.current;
      
      if (process.env.NODE_ENV === 'development') {
        console.group(`🚀 Performance Metrics: ${componentName}`);
        console.log(`Total Mount Time: ${totalTime}ms`);
        console.log(`Total Renders: ${renderCount.current}`);
        console.log(`Component Mounts: ${metrics.componentMounts}`);
        console.groupEnd();
      }
    };
  }, [componentName]);

  // Track memory usage (if available)
  useEffect(() => {
    if ('memory' in performance && (performance as any).memory) {
      const memory = (performance as any).memory;
      setMetrics(prev => ({
        ...prev,
        memoryUsage: memory.usedJSHeapSize / 1024 / 1024 // Convert to MB
      }));
    }
  }, []);

  // Use useMemo to track render count without causing infinite loops
  useMemo(() => {
    renderCount.current += 1;
    const renderEnd = Date.now();
    const renderTime = renderEnd - renderStart.current;
    renderStart.current = renderEnd;
    
    // Update metrics without triggering re-render if values haven't changed significantly
    setMetrics(prev => {
      if (Math.abs(prev.renderTime - renderTime) > 1 || prev.reRenders !== renderCount.current) {
        return {
          ...prev,
          renderTime,
          reRenders: renderCount.current
        };
      }
      return prev;
    });
  }, [componentName]);

  return metrics;
}

// Hook for measuring specific operations
export function useOperationTiming() {
  const startTiming = (operationName: string) => {
    const startTime = Date.now();
    
    return {
      end: () => {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`⏱️ ${operationName}: ${duration}ms`);
        }
        
        return duration;
      }
    };
  };

  return { startTiming };
}

// Hook for monitoring Core Web Vitals
export function useWebVitals() {
  const [vitals, setVitals] = useState<{
    FCP?: number;
    LCP?: number;
    FID?: number;
    CLS?: number;
  }>({});

  useEffect(() => {
    // Check if PerformanceObserver is supported
    if (typeof PerformanceObserver === 'undefined') {
      return;
    }

    const observers: PerformanceObserver[] = [];

    try {
      // First Contentful Paint
      const paintObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            setVitals(prev => ({ ...prev, FCP: entry.startTime }));
          }
        }
      });
      paintObserver.observe({ entryTypes: ['paint'] });
      observers.push(paintObserver);
    } catch (error) {
      console.warn('Failed to observe paint metrics:', error);
    }

    try {
      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        setVitals(prev => ({ ...prev, LCP: lastEntry.startTime }));
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      observers.push(lcpObserver);
    } catch (error) {
      console.warn('Failed to observe LCP metrics:', error);
    }

    try {
      // First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          setVitals(prev => ({ ...prev, FID: (entry as any).processingStart - entry.startTime }));
        }
      });
      fidObserver.observe({ entryTypes: ['first-input'] });
      observers.push(fidObserver);
    } catch (error) {
      console.warn('Failed to observe FID metrics:', error);
    }

    try {
      // Cumulative Layout Shift
      const clsObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        setVitals(prev => ({ ...prev, CLS: clsValue }));
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      observers.push(clsObserver);
    } catch (error) {
      console.warn('Failed to observe CLS metrics:', error);
    }

    return () => {
      observers.forEach(observer => {
        try {
          observer.disconnect();
        } catch (error) {
          console.warn('Failed to disconnect observer:', error);
        }
      });
    };
  }, []);

  return vitals;
}

// Hook for debouncing expensive operations
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Hook for throttling events
export function useThrottle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): T {
  const lastCall = useRef<number>(0);
  const lastCallTimeout = useRef<NodeJS.Timeout>();

  const throttledFn = useRef<T>(((...args: any[]) => {
    const now = Date.now();

    if (now - lastCall.current >= delay) {
      lastCall.current = now;
      return fn(...args);
    } else {
      clearTimeout(lastCallTimeout.current);
      lastCallTimeout.current = setTimeout(() => {
        lastCall.current = Date.now();
        return fn(...args);
      }, delay - (now - lastCall.current));
    }
  }) as T);

  return throttledFn.current;
}